import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { readPref, writePref, removePref } from '../utils/safeStorage';

const LocationContext = createContext(null);
const BRIDGE_SENTINEL = 'bridge';

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  // Consume location + permission data from AuthContext (which already fetched
  // /auth/me on mount). Avoids a duplicate /auth/me call and prevents the
  // re-render churn we used to get when getAuthHeaders was unstable.
  const { user, isAuthenticated, accessibleLocations, permissions, refetchCurrentUser } = useAuth();
  const [selectedLocationId, setSelectedLocationId] = useState(() => {
    const stored = readPref('chariot-selected-location');
    return stored === BRIDGE_SENTINEL ? null : (stored || null);
  });
  const [isBridgeMode, setIsBridgeMode] = useState(() => {
    return readPref('chariot-selected-location') === BRIDGE_SENTINEL;
  });
  const [loading, setLoading] = useState(true);
  // Whether we've done the one-time initial resolution of the selected location.
  // After that, the selection belongs to the user and must not be silently
  // re-resolved by a token refresh / re-auth re-render.
  const hasResolvedRef = useRef(false);

  // Derived: company + bridge access read from the AuthContext user object.
  // Bridge access now comes from the permission catalog. Falls back to legacy
  // user.bridge_access column for backward compat until that column is dropped.
  const company = user ? {
    id: user.company_id,
    name: user.company_name,
    slug: user.company_slug,
  } : null;
  const bridgeAccess = permissions?.['bridge.access'] === true || user?.bridge_access === true;
  // Permissions arrive a render AFTER `user` during login/2FA (setUser → awaited
  // /auth/me → setPermissions), so there's a window where permissions is {} and
  // bridgeAccess is briefly false. This flag lets the resolver tell "no bridge
  // access" apart from "permissions haven't loaded yet" so it won't wipe Bridge.
  const permissionsLoaded = !!permissions && Object.keys(permissions).length > 0;
  // Wrap in useMemo so the reference is stable across renders when the source
  // hasn't changed. Without this, `accessibleLocations || []` produces a new
  // `[]` every render, busting useCallback/useMemo deps downstream.
  const locations = useMemo(
    () => accessibleLocations || [],
    [accessibleLocations],
  );

  // Resolve the selected location ONCE, when auth + locations have settled.
  // After that the selection is the user's to change (via selectLocation) — a
  // token refresh / re-auth must never silently override it. The old version
  // re-ran on every bridgeAccess / locations change and, during the brief
  // empty-permissions window of a re-auth, treated Bridge as "no access", wiped
  // it, and defaulted to current_location_id (e.g. Orlando) hours later.
  useEffect(() => {
    if (!isAuthenticated) {
      hasResolvedRef.current = false;
      setLoading(false);
      return;
    }
    if (!user) return;
    if (hasResolvedRef.current) {
      setLoading(false);
      return;
    }

    const currentStored = readPref('chariot-selected-location');

    // Bridge selection.
    if (currentStored === BRIDGE_SENTINEL) {
      if (bridgeAccess) {
        setIsBridgeMode(true);
        setSelectedLocationId(null);
        hasResolvedRef.current = true;
        setLoading(false);
        return;
      }
      // bridgeAccess is false. If permissions haven't loaded yet, this is the
      // transient empty-permissions window — wait, don't wipe Bridge. Only fall
      // back once permissions are loaded and bridge access is truly absent.
      if (!permissionsLoaded) {
        setLoading(false);
        return;
      }
      removePref('chariot-selected-location');
      // fall through to choose a real location
    }

    // A real location selection needs the locations list to validate / default.
    if (locations.length === 0) {
      setLoading(false);
      return;
    }

    setIsBridgeMode(false);
    const validStored =
      currentStored && currentStored !== BRIDGE_SENTINEL && locations.some(l => l.id === currentStored)
        ? currentStored
        : null;
    if (validStored) {
      setSelectedLocationId(validStored);
    } else {
      const pref = user.current_location_id;
      const finalDefault = pref && locations.some(l => l.id === pref) ? pref : locations[0].id;
      setSelectedLocationId(finalDefault);
      writePref('chariot-selected-location', finalDefault);
    }
    hasResolvedRef.current = true;
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, locations.length, bridgeAccess, permissionsLoaded]);

  // Persist selected location to localStorage
  useEffect(() => {
    if (isBridgeMode) {
      writePref('chariot-selected-location', BRIDGE_SENTINEL);
    } else if (selectedLocationId) {
      writePref('chariot-selected-location', selectedLocationId);
    }
  }, [selectedLocationId, isBridgeMode]);

  // Select a different location (or Bridge mode with null)
  const selectLocation = useCallback((locationId) => {
    if (locationId === null || locationId === BRIDGE_SENTINEL) {
      // Bridge mode — requires bridge_access permission
      if (bridgeAccess) {
        setSelectedLocationId(null);
        setIsBridgeMode(true);
        writePref('chariot-selected-location', BRIDGE_SENTINEL);
      }
      return;
    }

    // Normal location selection
    const hasAccess = user?.role === 'admin' || locations.some(l => l.id === locationId);
    if (hasAccess || locations.length === 0) {
      setSelectedLocationId(locationId);
      setIsBridgeMode(false);
      writePref('chariot-selected-location', locationId);
    } else {
      console.warn('User does not have access to location:', locationId);
    }
  }, [locations, user?.role, bridgeAccess]);

  // Get the currently selected location object
  const selectedLocation = locations.find(l => l.id === selectedLocationId) || null;

  // Get headers to include location context in API requests
  const getLocationHeaders = useCallback(() => {
    return selectedLocationId ? { 'X-Location-Id': selectedLocationId } : {};
  }, [selectedLocationId]);

  // Check if user has access to a specific location
  const hasAccessToLocation = useCallback((locationId) => {
    if (user?.role === 'admin') return true;
    return locations.some(l => l.id === locationId);
  }, [locations, user?.role]);

  // Refresh locations — delegates to AuthContext.refetchCurrentUser, which
  // updates user, permissions, and accessibleLocations in one round-trip.
  // This component re-renders automatically when those values change.
  const refreshLocations = useCallback(async () => {
    if (!isAuthenticated) return;
    await refetchCurrentUser();
  }, [isAuthenticated, refetchCurrentUser]);

  const value = {
    // Location data
    locations,
    selectedLocationId,
    selectedLocation,
    loading,

    // Company data
    company,

    // Bridge mode
    isBridgeMode,
    hasBridgeAccess: bridgeAccess,

    // Actions
    selectLocation,
    refreshLocations,

    // Helpers
    getLocationHeaders,
    hasAccessToLocation,

    // Convenience flags
    isAdmin: user?.role === 'admin',
    hasMultipleLocations: locations.length > 1
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext;
